import type { MutableRefObject } from 'react';
import { useEffect, useState } from 'react';

import type { FilterGroups } from 'src/components/app/context/FilterContext';
import { useFilterContext } from 'src/components/app/context/FilterContext';

interface UseContainerOverflowProps {
  containerRef: MutableRefObject<HTMLDivElement | null>;
  resetButtonRef: MutableRefObject<HTMLButtonElement | null>;
}

interface GetOverflowCountProps {
  container: HTMLDivElement;
  resetButton: HTMLButtonElement;
  visibleFilterGroups: FilterGroups;
}

const getOverflowCount = ({
  container,
  resetButton,
  visibleFilterGroups,
}: GetOverflowCountProps): number => {
  const containerWidth = container.clientWidth;

  // Account for reset button always being visible:
  let currentWidth = resetButton.clientWidth;
  let count = 0;

  // Count how many items can fit.
  // "length - 1" so we don't count the reset button twice.
  for (let i = 0; i < container.children.length - 1; i++) {
    const child = container.children[i] as HTMLElement;

    currentWidth += child.offsetWidth;

    if (currentWidth > containerWidth) {
      // Divide i by two to account for AND/OR connectors
      count =
        Object.values(visibleFilterGroups).flat().length - Math.ceil(i / 2);

      break;
    }
  }

  return count;
};

/**
 * Calculate the amount of items overflowing the chip container.
 */
const useContainerOverflow = ({
  containerRef,
  resetButtonRef,
}: UseContainerOverflowProps): number => {
  const { visibleFilterGroups } = useFilterContext();
  const [overflowCount, setOverflowCount] = useState(0);

  useEffect(() => {
    // Reset every time the visible filters change so that all chips are
    //  rendered before calculating overflow.
    setOverflowCount(0);
  }, [visibleFilterGroups]);

  useEffect(() => {
    if (overflowCount) return; // prevent re-rendering loop

    if (containerRef.current && resetButtonRef.current)
      setOverflowCount(
        getOverflowCount({
          container: containerRef.current,
          resetButton: resetButtonRef.current,
          visibleFilterGroups,
        }),
      );
  }, [overflowCount, containerRef, resetButtonRef, visibleFilterGroups]);

  return overflowCount;
};

export default useContainerOverflow;
